
import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { MediaPickerModal } from "@/components/email-builder/MediaPickerModal";
import { BackgroundConfiguration } from "@/types/block-configurations";
import { ColorPicker } from "./ColorPicker";

interface BackgroundControlsProps {
  value: BackgroundConfiguration;
  onChange: (value: BackgroundConfiguration) => void;
}

/**
 * BackgroundControls Component
 * 
 * Controls for background color and image properties.
 * Includes color picker, image URL input, and image sizing options.
 * 
 * @param value - Current background configuration
 * @param onChange - Callback when background changes
 */
export function BackgroundControls({ value, onChange }: BackgroundControlsProps) {
  const [pickerOpen, setPickerOpen] = useState(false);

  const updateField = <K extends keyof BackgroundConfiguration>(
    field: K,
    newValue: BackgroundConfiguration[K]
  ) => {
    onChange({ ...value, [field]: newValue });
  };

  return (
    <div className="space-y-2.5">
      {/* Background Color */}
      <div>
        <Label className="text-xs font-medium mb-1 block">Background Color</Label>
        <ColorPicker
          value={value.color}
          onChange={(color) => updateField('color', color)}
        />
      </div>

      {/* Background Image URL */}
      <div>
        <Label className="text-xs font-medium mb-1 block">
          Background Image URL
          <span className="text-muted-foreground ml-1">(optional)</span>
        </Label>
        <div className="flex items-center gap-1.5">
          <Input
            type="url"
            value={value.imageUrl || ''}
            onChange={(e) => updateField('imageUrl', e.target.value || undefined)}
            placeholder="https://example.com/image.jpg"
            className="flex-1 min-w-0"
          />
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="shrink-0 rounded-md border border-input bg-background p-2 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            title="Pick from media library"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </button>
        </div>
        <MediaPickerModal
          isOpen={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onSelect={(url) => updateField('imageUrl', url)}
        />
      </div>

      {/* Image Size (only show if image URL is set) */}
      {value.imageUrl && (
        <div>
          <Label className="text-xs font-medium mb-1 block">Image Size</Label>
          <Select
            value={value.imageSize || 'cover'}
            onChange={(e) =>
              updateField('imageSize', e.target.value as BackgroundConfiguration['imageSize'])
            }
          >
            <option value="cover">Cover</option>
            <option value="contain">Contain</option>
            <option value="auto">Auto</option>
          </Select>
        </div>
      )}

      {/* Image Repeat (only show if image URL is set) */}
      {value.imageUrl && (
        <div>
          <Label className="text-xs font-medium mb-1 block">Image Repeat</Label>
          <Select
            value={value.imageRepeat || 'no-repeat'}
            onChange={(e) =>
              updateField('imageRepeat', e.target.value as BackgroundConfiguration['imageRepeat'])
            }
          >
            <option value="no-repeat">No Repeat</option>
            <option value="repeat">Repeat</option>
            <option value="repeat-x">Repeat X</option>
            <option value="repeat-y">Repeat Y</option>
          </Select>
        </div>
      )}

      {/* Preview (if image URL is set) */}
      {value.imageUrl && (
        <div>
          <Label className="text-xs font-medium mb-1 block">Preview</Label>
          <div
            className="w-full h-16 rounded-md border border-border"
            style={{
              backgroundColor: value.color,
              backgroundImage: `url(${value.imageUrl})`,
              backgroundSize: value.imageSize || 'cover',
              backgroundRepeat: value.imageRepeat || 'no-repeat',
              backgroundPosition: 'center',
            }}
            aria-label="Background preview"
          />
        </div>
      )}
    </div>
  );
}
